import {
    Box,
    Card,
    Stack,
    styled,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import columnShape from 'page-sections/approved-abstract/columnShape';
import HeadingArea from 'page-sections/approved-abstract/heading-area';
import SearchArea from 'page-sections/approved-abstract/search-area';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    useAsyncDebounce,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));
const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));
const ApprovedAbstract = () => {
    const [value, setValue] = useState('all');
    const [dataAbstract, setDataAbstract] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);
    const [header, setHeader] = useState([]);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: dataAbstract },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
    );

    const navigate = useNavigate();

    const getData = async (
        param,
        pageParam = pageNo,
        limitParam = pageSize,
        termParam = searchValue,
    ) => {
        await axiosInstance
            .get(`/api/prv/operator-submissions`, {
                params: {
                    type_id: param,
                    status: 'approved',
                    page: pageParam,
                    limit: limitParam,
                    ...(termParam ? { term: termParam } : {}),
                },
            })
            .then((res) => {
                const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
                const meta = Array.isArray(res.data) ? null : res.data?.meta || null;
                setDataAbstract(items);
                if (meta) {
                    setTotalPages(meta.totalPages || 0);
                } else {
                    setTotalPages(1);
                }
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        getData('all', 1, pageSize);
        getHeader();
        // eslint-disable-next-line
    }, []);

    const getHeader = async () => {
        await axiosInstance
            .get('api/prv/submission-types', {
                params: {
                    status: 'approved',
                }
            })
            .then((res) => setHeader(res.data))
            .catch((err) => console.log(err));
    };

    const handleChange = async (_, currentPageNo) => {
        setPageNo(currentPageNo);
        await getData(value, currentPageNo, pageSize, searchValue);
    }; // handle change for tab list

    const changeTab = (_, newValue) => {
        setValue(newValue);
        setPageNo(1);
        getData(newValue, 1, pageSize, searchValue);
    }; // search value state

    const [searchValue, setSearchValue] = useState(state.globalFilter);
    const handleSearch = useAsyncDebounce((term) => {
        setSearchValue(term);
        setPageNo(1);
        setGlobalFilter(term || undefined);
        getData(value, 1, pageSize, term);
    }, 200);

    const handleDetail = (data) => {
        navigate(`/dashboard/operator/submitted-abstract/${data.id}`);
    };

    return (
        <Box pt={2} pb={4}>
            <Card
                sx={{
                    py: 2,
                }}
            >
                <Box px={3}>
                    <HeadingArea
                        value={value}
                        changeTab={changeTab}
                        header={header}
                    />

                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                        typeId={value}
                        header={header}
                    />
                </Box>

                <Scrollbar autoHide={true}>
                    <Table
                        {...getTableProps()}
                        sx={{
                            minWidth: 900,
                        }}
                    >
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, index) => (
                                            <HeadTableCell
                                                key={index}
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps(),
                                                )}
                                                sx={{
                                                    paddingY: 0,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    borderBottom: 0,
                                                    width: column.width,
                                                    color: 'text.secondary',
                                                    minWidth: column.minWidth,
                                                    maxWidth: column.maxWidth,
                                                    '&:last-child': {
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {page.map((row, index) => {
                                prepareRow(row);
                                return (
                                    <TableRow
                                        {...row.getRowProps()}
                                        key={index}
                                    >
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <BodyTableCell
                                                    key={index}
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render('Cell', {
                                                        handleDetail,
                                                    })}
                                                </BodyTableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={pageNo}
                        count={totalPages}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default ApprovedAbstract;
